import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './FinancialAdminDashboard.css';

const FinancialAdminDashboard = ({ user }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [financialData, setFinancialData] = useState(null);
  const [costsData, setCostsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d'); // 7d, 30d, 90d, 12m

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    loadFinancialData();
    // Refresh every 5 minutes
    const interval = setInterval(loadFinancialData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      
      // Load financial data
      const [revenueResponse, costsResponse, usersResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/financial/revenue?range=${dateRange}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE_URL}/api/admin/financial/costs?range=${dateRange}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE_URL}/api/admin/financial/users-analytics?range=${dateRange}`, {
          credentials: 'include'
        })
      ]);

      if (revenueResponse.ok && costsResponse.ok && usersResponse.ok) {
        const [revenue, costs, users] = await Promise.all([
          revenueResponse.json(),
          costsResponse.json(),
          usersResponse.json()
        ]);

        setFinancialData({
          revenue,
          users,
          summary: {
            totalRevenue: revenue.total || 0,
            totalCosts: costs.total || 0,
            profit: (revenue.total || 0) - (costs.total || 0),
            marginPercentage: revenue.total > 0 ? (((revenue.total - costs.total) / revenue.total) * 100).toFixed(1) : 0,
            hasRealData: revenue.hasRealData && costs.hasRealData
          }
        });

        setCostsData(costs);
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateDemoData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/financial/seed-demo-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(t('financialAdminDashboard.demoDataGenerated', { 
          costs: result.generated.costs, 
          revenues: result.generated.revenues 
        }));
        loadFinancialData(); // Reload data
      } else {
        const error = await response.json();
        toast.error(t('financialAdminDashboard.error', { message: error.error }));
      }
    } catch (error) {
      console.error('Error generating demo data:', error);
      toast.error(t('financialAdminDashboard.demoDataError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num || 0);
  };

  if (!user?.isSuperAdmin) {
    return (
      <div className="financial-admin-access-denied">
        <h2>🚫 Accès Restreint</h2>
        <p>Cette interface est réservée aux Super Administrateurs.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="financial-admin-loading">
        <div className="financial-loading-spinner"></div>
        <h2>{t('financialAdminDashboard.loadingData')}</h2>
        <p>{t('financialAdminDashboard.loadingDescription')}</p>
      </div>
    );
  }

  return (
    <div className="financial-admin-dashboard" id="financial-admin-dashboard">
      <header className="financial-header">
        <div className="financial-header-main">
          <h1>{t('financialAdminDashboard.title')}</h1>
          <p>
            {t('financialAdminDashboard.subtitle')}
            {financialData?.summary?.hasRealData ? (
              <span className="data-indicator real-data">{t('financialAdminDashboard.realData')}</span>
            ) : (
              <span className="data-indicator estimated-data">{t('financialAdminDashboard.estimatedData')}</span>
            )}
          </p>
        </div>
        <div className="financial-controls">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="financial-date-selector"
          >
            <option value="7d">{t('financialAdminDashboard.dateRanges.7d')}</option>
            <option value="30d">{t('financialAdminDashboard.dateRanges.30d')}</option>
            <option value="90d">{t('financialAdminDashboard.dateRanges.90d')}</option>
            <option value="12m">{t('financialAdminDashboard.dateRanges.12m')}</option>
          </select>
          <button 
            onClick={generateDemoData}
            disabled={loading}
            className="financial-demo-btn"
            title={t('financialAdminDashboard.generateDemo')}
          >
            {loading ? '⏳' : '🎯'} {t('financialAdminDashboard.generateDemoBtn')}
          </button>
          <button 
            onClick={loadFinancialData}
            className="financial-refresh-btn"
            title={t('financialAdminDashboard.refreshData')}
          >
            🔄 {t('financialAdminDashboard.refreshBtn')}
          </button>
        </div>
      </header>

      {/* Navigation des onglets */}
      <nav className="financial-nav">
        <button 
          className={`financial-nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 {t('financialAdminDashboard.tabs.overview')}
        </button>
        <button 
          className={`financial-nav-btn ${activeTab === 'costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('costs')}
        >
          💸 {t('financialAdminDashboard.tabs.costs')}
        </button>
        <button 
          className={`financial-nav-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 {t('financialAdminDashboard.tabs.users')}
        </button>
        <button 
          className={`financial-nav-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          📋 {t('financialAdminDashboard.tabs.reports')}
        </button>
      </nav>

      <main className="financial-content">
        {activeTab === 'overview' && (
          <div className="financial-overview">
            <h2>{t('financialAdminDashboard.businessOverview')}</h2>
            
            {/* KPIs principaux */}
            <div className="financial-kpis">
              <div className="financial-kpi revenue">
                <div className="kpi-icon">💰</div>
                <div className="kpi-content">
                  <h3>{t('financialAdminDashboard.totalRevenue')}</h3>
                  <div className="kpi-value">{formatCurrency(financialData?.summary?.totalRevenue)}</div>
                  <div className="kpi-period">{t('financialAdminDashboard.overPeriod', { 
                    period: dateRange === '7d' ? t('financialAdminDashboard.periods.7days') : 
                            dateRange === '30d' ? t('financialAdminDashboard.periods.30days') : 
                            dateRange === '90d' ? t('financialAdminDashboard.periods.90days') : 
                            t('financialAdminDashboard.periods.12months')
                  })}</div>
                </div>
              </div>

              <div className="financial-kpi costs">
                <div className="kpi-icon">💸</div>
                <div className="kpi-content">
                  <h3>{t('financialAdminDashboard.totalCosts')}</h3>
                  <div className="kpi-value">{formatCurrency(financialData?.summary?.totalCosts)}</div>
                  <div className="kpi-breakdown">
                    OpenAI: {formatCurrency(costsData?.openai || 0)} | Neo4j: {formatCurrency(costsData?.neo4j || 0)}
                  </div>
                </div>
              </div>

              <div className="financial-kpi profit">
                <div className="kpi-icon">{financialData?.summary?.profit >= 0 ? '📈' : '📉'}</div>
                <div className="kpi-content">
                  <h3>{t('financialAdminDashboard.profitLoss')}</h3>
                  <div className={`kpi-value ${financialData?.summary?.profit >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(financialData?.summary?.profit)}
                  </div>
                  <div className="kpi-margin">
                    {t('financialAdminDashboard.margin')}: {financialData?.summary?.marginPercentage}%
                  </div>
                </div>
              </div>

              <div className="financial-kpi users">
                <div className="kpi-icon">👥</div>
                <div className="kpi-content">
                  <h3>{t('financialAdminDashboard.activeUsers')}</h3>
                  <div className="kpi-value">{formatNumber(financialData?.users?.total)}</div>
                  <div className="kpi-breakdown">
                    {t('financialAdminDashboard.payingUsers')}: {formatNumber(financialData?.users?.paying)} | {t('financialAdminDashboard.activeTrials')}: {formatNumber(financialData?.users?.trial)}
                  </div>
                  {financialData?.users?.totals && (
                    <div className="kpi-detail">
                      ({formatNumber(financialData.users.totals.allTrials)} {t('financialAdminDashboard.totalTrials')})
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Graphiques revenus/coûts */}
            <div className="financial-charts">
              <div className="financial-chart-container">
                <h3>📊 Évolution Revenus vs Coûts</h3>
                <div className="chart-placeholder">
                  <p>Graphique des revenus et coûts sur la période</p>
                  <small>📝 TODO: Intégrer Chart.js pour visualisations</small>
                </div>
              </div>

              <div className="financial-chart-container">
                <h3>🎯 Répartition des coûts</h3>
                <div className="costs-breakdown">
                  <div className="cost-item">
                    <span className="cost-label">🤖 OpenAI API</span>
                    <span className="cost-amount">{formatCurrency(costsData?.openai)}</span>
                    <span className="cost-percentage">{costsData?.total > 0 ? ((costsData?.openai / costsData?.total) * 100).toFixed(1) : 0}%</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">🗄️ Neo4j</span>
                    <span className="cost-amount">{formatCurrency(costsData?.neo4j)}</span>
                    <span className="cost-percentage">{costsData?.total > 0 ? ((costsData?.neo4j / costsData?.total) * 100).toFixed(1) : 0}%</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">☁️ Infrastructure</span>
                    <span className="cost-amount">{formatCurrency(costsData?.infrastructure)}</span>
                    <span className="cost-percentage">{costsData?.total > 0 ? ((costsData?.infrastructure / costsData?.total) * 100).toFixed(1) : 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alertes et recommandations */}
            <div className="financial-alerts">
              <h3>⚠️ Alertes et Recommandations</h3>
              <div className="alerts-list">
                {financialData?.summary?.marginPercentage < 30 && (
                  <div className="alert warning">
                    <span className="alert-icon">⚠️</span>
                    <span className="alert-message">Marge faible ({financialData?.summary?.marginPercentage}%) - Optimiser les coûts ou augmenter les prix</span>
                  </div>
                )}
                {costsData?.openai > 1000 && (
                  <div className="alert info">
                    <span className="alert-icon">💡</span>
                    <span className="alert-message">Coûts OpenAI élevés - Considérer l'optimisation des prompts ou cache des réponses</span>
                  </div>
                )}
                {financialData?.users?.trial > financialData?.users?.paying * 3 && (
                  <div className="alert success">
                    <span className="alert-icon">🎯</span>
                    <span className="alert-message">Beaucoup d'utilisateurs trial - Opportunité de conversion importante</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="financial-costs">
            <h2>💸 Gestion des Coûts Opérationnels</h2>
            <p>📋 TODO: Interface détaillée de gestion des coûts OpenAI, Neo4j et infrastructure</p>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="financial-users">
            <h2>👥 Analytics Utilisateurs Payants</h2>
            <p>📋 TODO: Analytics détaillées des utilisateurs, conversion, rentabilité</p>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="financial-reports">
            <h2>📋 Rapports Financiers</h2>
            <p>📋 TODO: Export de rapports, réconciliation, automatisation</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FinancialAdminDashboard;
